import { Image as ImageIcon, Info, X } from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { API_URLS } from "@/config/urls";
import {
  loadWolfProfile,
  requestWolfEmailChange,
  saveWolfProfile,
  uploadWolfCoverImage,
  uploadWolfProfileImage,
  type WolfProfile,
  type WolfProfileUpdate,
} from "@/lib/wolf-api/profile";

interface MemberInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  loadProfile?: () => Promise<WolfProfile>;
  saveProfile?: (input: WolfProfileUpdate) => Promise<WolfProfile>;
  uploadProfileImage?: (file: File) => Promise<string>;
  uploadCoverImage?: (file: File) => Promise<string>;
  requestEmailChange?: (input: {
    email: string;
    currentPassword: string;
  }) => Promise<void>;
}

const PLAN_LABELS: Record<string, string> = {
  a: "Basic",
  b: "Plus",
  c: "Edu",
  d: "Biz",
};
const IMAGE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp";
const createPreviewUrl = (file: File | null) =>
  file && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(file)
    : undefined;

export const MemberInformationModal = ({
  isOpen,
  onClose,
  loadProfile = loadWolfProfile,
  saveProfile = saveWolfProfile,
  uploadProfileImage = uploadWolfProfileImage,
  uploadCoverImage = uploadWolfCoverImage,
  requestEmailChange = requestWolfEmailChange,
}: MemberInformationModalProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const profileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<WolfProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [language, setLanguage] = useState<"ja" | "en">("ja");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(
    null,
  );
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(
    null,
  );
  const [isDiscardPromptOpen, setIsDiscardPromptOpen] = useState(false);
  const [isEmailChangeOpen, setIsEmailChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [isEmailSaving, setIsEmailSaving] = useState(false);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);

  const profilePreviewUrl = useMemo(
    () => createPreviewUrl(selectedProfileImage) ?? profile?.profileImageUrl,
    [profile?.profileImageUrl, selectedProfileImage],
  );
  const coverPreviewUrl = useMemo(
    () => createPreviewUrl(selectedCoverImage) ?? profile?.coverImageUrl,
    [profile?.coverImageUrl, selectedCoverImage],
  );

  useEffect(
    () => () => {
      if (typeof URL.revokeObjectURL !== "function") return;
      if (selectedProfileImage && profilePreviewUrl)
        URL.revokeObjectURL(profilePreviewUrl);
      if (selectedCoverImage && coverPreviewUrl)
        URL.revokeObjectURL(coverPreviewUrl);
    },
    [
      coverPreviewUrl,
      profilePreviewUrl,
      selectedCoverImage,
      selectedProfileImage,
    ],
  );

  const hasUnsavedChanges = Boolean(
    profile &&
    (firstName.trim() !== profile.firstName ||
      lastName.trim() !== profile.lastName ||
      language !== profile.language ||
      selectedProfileImage ||
      selectedCoverImage),
  );
  const hasOutstandingQualityIssues = Boolean(
    profile?.profileQualityIssues?.required.length ||
    profile?.profileQualityIssues?.recommended.length,
  );
  const canSubmit =
    (hasUnsavedChanges || hasOutstandingQualityIssues) &&
    Boolean(firstName.trim() && lastName.trim()) &&
    !isSaving;
  const planLabels =
    profile?.grantedLicenseTypes.map((type) => PLAN_LABELS[type] ?? type) ?? [];
  const displayedPlans =
    planLabels.length > 1
      ? planLabels.filter((plan) => plan !== "Basic")
      : planLabels;
  const isOnlyBasic = planLabels.length === 1 && planLabels[0] === "Basic";
  const requiredIssues = profile?.profileQualityIssues?.required ?? [];
  const recommendedIssues = profile?.profileQualityIssues?.recommended ?? [];
  const nameNeedsAttention = requiredIssues.some((issue) =>
    ["name_missing", "name_review_required"].includes(issue),
  );
  const hasOptionalIssues = [...requiredIssues, ...recommendedIssues].some(
    (issue) => ["language_missing", "avatar_missing"].includes(issue),
  );

  const requestClose = () => {
    if (isSaving) return;
    if (hasUnsavedChanges) return setIsDiscardPromptOpen(true);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setIsLoading(true);
    setError(null);
    void loadProfile()
      .then((loaded) => {
        if (!active) return;
        setProfile(loaded);
        setFirstName(loaded.firstName);
        setLastName(loaded.lastName);
        setLanguage(loaded.language);
        setSelectedProfileImage(null);
        setSelectedCoverImage(null);
      })
      .catch(() => active && setError(t("common.memberInformationLoadError")))
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen, loadProfile, t]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile || !canSubmit) return;
    setIsSaving(true);
    setError(null);
    try {
      const [profileImageSignedBlobId, coverImageSignedBlobId] =
        await Promise.all([
          selectedProfileImage
            ? uploadProfileImage(selectedProfileImage)
            : undefined,
          selectedCoverImage ? uploadCoverImage(selectedCoverImage) : undefined,
        ]);
      const updated = await saveProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        language,
        ...(profileImageSignedBlobId ? { profileImageSignedBlobId } : {}),
        ...(coverImageSignedBlobId ? { coverImageSignedBlobId } : {}),
      });
      setProfile(updated);
      setSelectedProfileImage(null);
      setSelectedCoverImage(null);
      onClose();
    } catch {
      setError(t("common.memberInformationSaveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = async () => {
    if (!newEmail.trim() || !currentPassword || isEmailSaving) return;
    setIsEmailSaving(true);
    setEmailNotice(null);
    try {
      await requestEmailChange({
        email: newEmail.trim().toLowerCase(),
        currentPassword,
      });
      setNewEmail("");
      setCurrentPassword("");
      setEmailNotice(t("common.confirmationEmailSent"));
    } catch {
      setEmailNotice(t("common.emailChangeRequestError"));
    } finally {
      setIsEmailSaving(false);
    }
  };

  return (
    <BasicModal
      isOpen={isOpen}
      onClose={requestClose}
      title={t("common.memberInformation")}
      maxWidth="max-w-2xl"
      showCloseButton={false}
      overlayClassName="border-0 bg-black/50 backdrop-blur-none"
      contentClassName="max-h-[calc(100vh-2rem)] overflow-hidden rounded-2xl shadow-xl sm:rounded-2xl"
    >
      <div className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-medium leading-7 text-gray-950">
            {t("common.memberInformation")}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            aria-label={t("common.close")}
            className="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-rw-main disabled:opacity-50"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {isLoading && (
            <p className="text-sm text-gray-500">{t("common.loading")}</p>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-md bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </p>
          )}
          {profile && (
            <form id={formId} onSubmit={handleSubmit}>
              <section className="pb-3">
                <h3 className="mb-4 text-base font-semibold text-gray-900">
                  {t("common.profile")}
                </h3>
                {(nameNeedsAttention || hasOptionalIssues) && (
                  <div className="mb-4 space-y-2">
                    {nameNeedsAttention && (
                      <Notice tone="warning">
                        {t("common.profileNameNeedsAttention")}
                      </Notice>
                    )}
                    {hasOptionalIssues && (
                      <Notice tone="info">
                        {t("common.optionalProfileSettingsDescription")}
                      </Notice>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept={IMAGE_ACCEPT}
                    aria-label={`${t("common.coverImage")}ファイル`}
                    className="hidden"
                    onChange={(event) =>
                      setSelectedCoverImage(
                        event.currentTarget.files?.[0] ?? null,
                      )
                    }
                  />
                  <input
                    ref={profileInputRef}
                    type="file"
                    accept={IMAGE_ACCEPT}
                    aria-label={`${t("common.profileImage")}ファイル`}
                    className="hidden"
                    onChange={(event) =>
                      setSelectedProfileImage(
                        event.currentTarget.files?.[0] ?? null,
                      )
                    }
                  />
                  <div className="relative pb-9">
                    <button
                      type="button"
                      aria-label={t("common.coverImage")}
                      className="group relative h-32 w-full overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 text-left sm:h-36"
                      onClick={() => coverInputRef.current?.click()}
                    >
                      {coverPreviewUrl ? (
                        <img
                          src={coverPreviewUrl}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        <ImagePlaceholder label={t("common.selectImage")} />
                      )}
                      <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15 group-focus-visible:bg-black/15" />
                    </button>
                    <button
                      type="button"
                      aria-label={t("common.profileImage")}
                      className="group absolute bottom-0 left-4 size-20 overflow-hidden rounded-full border-4 border-white bg-gray-50 text-left shadow-sm sm:left-5 sm:size-24"
                      onClick={() => profileInputRef.current?.click()}
                    >
                      {profilePreviewUrl ? (
                        <img
                          src={profilePreviewUrl}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        <ImagePlaceholder
                          compact
                          label={t("common.selectImage")}
                        />
                      )}
                      <div className="absolute inset-0 rounded-full bg-black/0 transition-colors group-hover:bg-black/15 group-focus-visible:bg-black/15" />
                    </button>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-500">
                    {t("common.profileImageFormats")}
                  </p>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <span>{t("common.emailAddress")}</span>
                      <input
                        className="mt-1 h-11 w-full rounded-xl border-[1.5px] border-gray-200 bg-gray-100 p-2.5 text-base font-normal leading-6 text-gray-500 opacity-60"
                        value={profile.email}
                        readOnly
                      />
                    </label>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 rounded-full border-2 border-gray-200 bg-gray-50 px-2.5 text-base font-semibold leading-6 text-gray-600 hover:bg-gray-200"
                        onClick={() => setIsEmailChangeOpen(true)}
                      >
                        {t("common.changeEmailAddress")}
                      </Button>
                    </div>
                    {emailNotice && (
                      <p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                        {emailNotice}
                      </p>
                    )}
                    {isEmailChangeOpen && (
                      <div className="space-y-3">
                        <ProfileInput
                          label={t("common.newEmailAddress")}
                          value={newEmail}
                          onChange={setNewEmail}
                        />
                        <label className="block space-y-1 text-sm font-medium text-gray-700">
                          <span>{t("common.currentPassword")}</span>
                          <input
                            type="password"
                            className="h-11 w-full rounded-xl border-[1.5px] border-gray-100 bg-white p-2.5 text-base font-normal leading-6 text-gray-700 shadow-none hover:border-rw-sub focus:border-rw-main focus:outline-none"
                            value={currentPassword}
                            onChange={(event) =>
                              setCurrentPassword(event.target.value)
                            }
                          />
                        </label>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 rounded-full px-2.5 text-base font-semibold leading-6"
                            onClick={() => setIsEmailChangeOpen(false)}
                          >
                            {t("common.cancel")}
                          </Button>
                          <Button
                            type="button"
                            variant="rw-sub"
                            size="sm"
                            className="h-7 rounded-full px-2.5 text-base font-semibold leading-6"
                            isLoading={isEmailSaving}
                            disabled={!newEmail.trim() || !currentPassword}
                            onClick={() => void handleEmailChange()}
                          >
                            {t("common.sendConfirmationEmail")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <ProfileInput
                      label={`${t("common.lastName")} *`}
                      value={lastName}
                      onChange={setLastName}
                    />
                    <ProfileInput
                      label={`${t("common.firstName")} *`}
                      value={firstName}
                      onChange={setFirstName}
                    />
                  </div>
                  <label className="block space-y-1 text-sm font-medium text-gray-700">
                    <span>{t("common.language")} *</span>
                    <select
                      className="h-11 w-full rounded-xl border-[1.5px] border-gray-100 bg-white p-2.5 text-base font-normal leading-6 text-gray-700 shadow-none hover:border-rw-sub focus:border-rw-main focus:outline-none"
                      value={language}
                      onChange={(event) =>
                        setLanguage(event.target.value === "en" ? "en" : "ja")
                      }
                    >
                      <option value="ja">日本語</option>
                      <option value="en">English</option>
                    </select>
                  </label>
                </div>
                <div className="mt-6 h-px bg-gray-200" />
              </section>

              <section className="py-3">
                <h3 className="mb-4 text-base font-semibold text-gray-900">
                  {t("common.contractStatus")}
                </h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="mb-2 font-medium text-gray-900">
                    {t("common.currentPlans")}
                  </div>
                  {displayedPlans.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {displayedPlans.map((plan) => (
                        <span
                          key={plan}
                          className="border-rw-main/30 bg-rw-main/5 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold text-rw-main"
                        >
                          {plan}
                        </span>
                      ))}
                    </div>
                  ) : (
                    "-"
                  )}
                  {isOnlyBasic && (
                    <div className="border-rw-main/30 bg-rw-main/5 rounded-lg border p-3">
                      <div className="font-medium text-gray-900">
                        {t("common.moreFeaturesAvailable")}
                      </div>
                      <a
                        className="mt-1 inline-flex font-medium text-rw-main hover:underline"
                        href={`${API_URLS.WHALE}/subscription?display_basic=false`}
                      >
                        {t("common.upgradeToPlus")}
                      </a>
                      <p className="mt-2 text-gray-600">
                        {t("common.organizationPlanHelp")}
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {hasUnsavedChanges && (
                <Notice tone="warning">{t("common.unsavedChanges")}</Notice>
              )}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="rw-sub"
                    size="sm"
                    isLoading={isSaving}
                    disabled={!canSubmit}
                    className="h-7 rounded-full px-2.5 text-base font-semibold leading-6"
                  >
                    {t("common.save")}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <ConfirmationDialog
        open={isDiscardPromptOpen}
        onOpenChange={setIsDiscardPromptOpen}
        title={t("common.discardChangesTitle")}
        description={t("common.discardMemberInformationDescription")}
        confirmText={t("common.discardChanges")}
        cancelText={t("common.continueEditing")}
        variant="destructive"
        closeOnConfirm
        onConfirm={onClose}
      />
    </BasicModal>
  );
};

const ProfileInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <label className="space-y-1 text-sm font-medium text-gray-700">
    <span>{label}</span>
    <input
      className="h-11 w-full rounded-xl border-[1.5px] border-gray-100 bg-white p-2.5 text-base font-normal leading-6 text-gray-700 shadow-none hover:border-rw-sub focus:border-rw-main focus:outline-none"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required
    />
  </label>
);

const ImagePlaceholder = ({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) => (
  <div
    className={`flex size-full items-center justify-center gap-2 text-xs text-gray-500 ${compact ? "flex-col px-2 text-center" : ""}`}
  >
    <ImageIcon className="size-5" />
    <span>{label}</span>
  </div>
);

const Notice = ({
  tone,
  children,
}: {
  tone: "warning" | "info";
  children: ReactNode;
}) => (
  <div
    className={`flex items-start gap-2 rounded-lg border p-3 text-xs leading-relaxed ${tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-950" : "border-blue-200 bg-blue-50 text-blue-950"}`}
  >
    <Info className="mt-0.5 size-4 shrink-0" />
    <span>{children}</span>
  </div>
);
